import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findIconDefinition, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { useIconsLoaded } from '@v2/icons';

type Props = {
  /** An icon name as the data holds it — `star`, `book-open`, `home`. */
  name?: string | null;
  prefix?: IconPrefix;
  className?: string;
  fixedWidth?: boolean;
};

/**
 * An icon chosen by name at runtime, rather than by import at build time.
 *
 * Use it wherever the icon comes from data — jss_sections.icon today. Icons known
 * statically should keep importing their definition and rendering FontAwesomeIcon
 * directly; those tree-shake, and none of this applies to them.
 *
 * Rendering `<FontAwesomeIcon icon={['fal', name]} />` for a data-driven name is
 * unsafe. FontAwesome resolves a name once, at render, and when it finds nothing it
 * logs and returns null — a hole in the layout rather than a fallback. Two situations
 * produce that, and both look like a broken page:
 *
 *   - the icon asset has not arrived yet
 *   - the name is not in the curated set, because something was given an icon we do
 *     not ship
 *
 * Both draw the generic icon here, and subscribing to the load means the first
 * corrects itself the moment the icons register.
 */
export default function NamedIcon({ name, prefix = 'fal', className, fixedWidth }: Props) {
  const loaded = useIconsLoaded();

  const icon = name && loaded ? findIconDefinition({ prefix, iconName: name as never }) : undefined;

  return <FontAwesomeIcon icon={icon || faInfoCircle} className={className} fixedWidth={fixedWidth} />;
}
